'use client';

import { useBikeFilter } from 'context/BikeFilterContext';
import type { RideType, UserBike } from 'models/shared';
import { useEffect, useRef } from 'react';

type BikeFilterFields = {
  bikeYears?: string[];
  bikeMakes?: string[];
  bikeModels?: string[];
  bikeTypes?: string[];
  rideTypes?: RideType[];
};

export type FitmentBike = Pick<UserBike, 'year' | 'make' | 'model'> & {
  type?: RideType;
};

/**
 * The vehicle the results are actually narrowed down by, or null when the
 * filters only carry a partial vehicle (a make with no model, for instance).
 */
export const getFilteredBike = (
  filters: BikeFilterFields
): FitmentBike | null => {
  const year = filters.bikeYears?.[0] ? Number(filters.bikeYears[0]) : NaN;
  const make = filters.bikeMakes?.[0];
  const model = filters.bikeModels?.[0];

  if (!year || Number.isNaN(year) || !make || !model) return null;

  return {
    year,
    make: make as UserBike['make'],
    model,
    type: (filters.bikeTypes?.[0] ?? filters.rideTypes?.[0]) as
      | RideType
      | undefined,
  };
};

const isSameBike = (
  a: Partial<FitmentBike> | undefined,
  b: Partial<FitmentBike> | undefined
) => a?.year === b?.year && a?.make === b?.make && a?.model === b?.model;

/**
 * Keeps the vehicle shown in the "Find parts that fit" bar and the vehicle the
 * results are filtered by pointing at the same bike.
 *
 * The vehicle lives in `BikeFilterContext`, which survives client navigations,
 * while the filtering lives in the url. Either one can arrive without the
 * other — a suggestion link drops the query params, a shared url arrives with
 * no context — so whichever side is missing is filled in from the other.
 */
export const useFitmentFilterSync = ({
  filters,
  onBikeComplete,
  enabled = true,
  autoApply = true,
}: {
  filters: BikeFilterFields;
  onBikeComplete: (bike: UserBike) => void;
  enabled?: boolean;
  /** whether a vehicle carried over from another page filters this one on its own */
  autoApply?: boolean;
}) => {
  const {
    selectedBike,
    setSelectedBike,
    isFitmentFilterEnabled,
    setFitmentFilterEnabled,
  } = useBikeFilter();

  const filteredBike = getFilteredBike(filters);

  // Back and forward restore a url as it was. One without the bike is a page
  // the shopper saw unfiltered, and putting the bike back would push a new
  // entry on top of the one they just went back to.
  const historyNavigation = useRef(false);
  useEffect(() => {
    const onPopState = () => {
      historyNavigation.current = true;
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useEffect(() => {
    if (!enabled) return;

    const restored = historyNavigation.current;
    historyNavigation.current = false;

    if (filteredBike) {
      if (!isSameBike(selectedBike, filteredBike)) {
        setSelectedBike(filteredBike);
      }
      return;
    }

    if (!autoApply) return;
    if (!isFitmentFilterEnabled) return;
    if (!selectedBike.year || !selectedBike.make || !selectedBike.model) return;

    // The bike stays in the bar, unchecked, to be turned back on.
    if (restored) {
      setFitmentFilterEnabled(false);
      return;
    }

    onBikeComplete(selectedBike as UserBike);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enabled,
    autoApply,
    filteredBike?.year,
    filteredBike?.make,
    filteredBike?.model,
    isFitmentFilterEnabled,
    selectedBike.year,
    selectedBike.make,
    selectedBike.model,
  ]);

  return {
    filteredBike,
    /** the bike the fitment bar should show, filtered or merely remembered */
    displayedBike: filteredBike ?? selectedBike,
    isFilteringByBike: !!filteredBike,
  };
};
